import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { PlusIcon } from '@phosphor-icons/react'

import { Button, ScrollableTabs, Text, ToastContext } from '@reveni-io/reveni-ui'

import QueryFilter from '@/common/components/QueryFilter/QueryFilter'
import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useDragSort from '@/common/hooks/useDragSort/useDragSort'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'

import DeletePolicyRuleModal from './DeletePolicyRuleModal/DeletePolicyRuleModal'
import usePolicyRules from './hooks/usePolicyRules/usePolicyRules'
import PolicyRuleItemList from './PolicyRuleItemList/PolicyRuleItemList'
import ShowMoreResults from './ShowMoreResults/ShowMoreResults'
import { emptyTabMessages } from './constants'
import { getPolicyRuleTabs } from './helpers'
import * as Styles from './styles'

const PolicyRulesList = () => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const { hasPermission } = useUserPermissions()

  const {
    policyRules,
    gettingPolicyRules,
    setSearch,
    handleChangeEnabled,
    page,
    count,
    pageSize,
    handleShowMore,
    ruleToDelete,
    setRuleToDelete,
    confirmDeleteRule,
    cancelDeleteRule,
    navigateToDetail,
    navigateToDuplicate,
    navigateToCreate,
    reorderRules,
    hasChanges,
    revertChanges,
    onSave,
    reorderingPolicyRules,
    summary,
    selectedState,
    setSelectedState,
    isEmpty,
    isSelectedTabEmpty,
  } = usePolicyRules({
    storeId: info?.selectedStore?.id,
    setToastInfo,
  })

  const { dragging, onSortStart, onSortEnd } = useDragSort(reorderRules)
  const isEditable = hasPermission(userPermissions.editPolicyRules)

  return (
    <Styles.MainContainer>
      {hasChanges && <UnsavedChangesAlert hasPermission={isEditable} />}
      <Text type="bodyMRegular">
        {t(
          'Define reglas para ampliar o reemplazar tu política de devoluciones predeterminada aplicando acciones específicas a las solicitudes que cumplan tus condiciones:'
        )}
      </Text>
      {!isEmpty && <QueryFilter onChange={setSearch} placeholder={t('Nombre de regla, ID, descripción')} />}
      {!isEmpty && (
        <ScrollableTabs tabs={getPolicyRuleTabs(t, summary)} value={selectedState} onChange={setSelectedState} />
      )}
      {isSelectedTabEmpty && <Text type="bodyMRegular">{t(emptyTabMessages[selectedState])}</Text>}
      {!isSelectedTabEmpty && (
        <PolicyRuleItemList
          rules={policyRules}
          loading={gettingPolicyRules}
          onChangeEnabled={handleChangeEnabled}
          onEdit={navigateToDetail}
          onDuplicate={navigateToDuplicate}
          onDelete={setRuleToDelete}
          disabled={!isEditable}
          dragging={dragging}
          onSortStart={onSortStart}
          onSortEnd={onSortEnd}
        />
      )}
      {!isEmpty && !isSelectedTabEmpty && (
        <ShowMoreResults count={count} page={page} pageSize={pageSize} onShowMore={handleShowMore} />
      )}
      {isEditable && (
        <div>
          <Button variant="text" size="s" onClick={navigateToCreate}>
            <PlusIcon size={24} />
            {t('Añadir nueva regla')}
          </Button>
        </div>
      )}
      <SaveChangesButtons
        hasChanges={isEditable && hasChanges}
        onSave={onSave}
        onRevertChanges={revertChanges}
        loading={reorderingPolicyRules}
        disabled={reorderingPolicyRules}
      />
      <DeletePolicyRuleModal show={!!ruleToDelete} onClose={cancelDeleteRule} onDelete={confirmDeleteRule} />
    </Styles.MainContainer>
  )
}

export default PolicyRulesList
